import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { listProductNdcsApi, setDefaultNdcForProductApi } from 'api/inventory';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { handleError } from 'store/thunks/errorHandlerThunks';

const EditDefaultNdcDialog = ({ open, productId, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // POM-5707 — options come from listProductNdcs, not from the inventory grid.
  // The grid is scoped to stocked/facility-associated inventory, so deriving the
  // list from it hides every NDC of the product that isn't stocked anywhere. The
  // default NDC is a dispensing preference, not a stock gate, so an unstocked
  // NDC is a legitimate choice and has to be offered here.
  const [ndcOptions, setNdcOptions] = useState([]);
  const [currentDefaultNdc, setCurrentDefaultNdc] = useState('');
  const [selectedNdc, setSelectedNdc] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const [reloadToken, setReloadToken] = useState(0);
  const retryLoad = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!open || !productId) return undefined;

    // Guards against a stale response landing after the dialog was closed or
    // reopened for a different product.
    let cancelled = false;

    setLoading(true);
    setLoadError('');
    setNdcOptions([]);
    setCurrentDefaultNdc('');
    setSelectedNdc('');

    listProductNdcsApi(productId)
      .then((options) => {
        if (cancelled) return;
        const existingDefault = options.find((option) => option.isDefault)?.ndc ?? '';
        setNdcOptions(options);
        setCurrentDefaultNdc(existingDefault);
        setSelectedNdc(existingDefault);
      })
      .catch((error) => {
        if (cancelled) return;
        // Keep the dialog open and offer a retry rather than closing on a failed
        // read — same no-silent-failure handling as the save path (POM-5739).
        setLoadError(getGraphQLErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, productId, reloadToken]);

  const handleConfirm = async () => {
    if (!selectedNdc || selectedNdc === currentDefaultNdc) return;
    setSaving(true);
    try {
      await setDefaultNdcForProductApi({ productId, ndc: selectedNdc });
      enqueueSnackbar(`Default NDC set to ${selectedNdc}`, { variant: 'success' });
      onSaved?.();
      onClose?.();
    } catch (error) {
      enqueueSnackbar('Failed to update default NDC', { variant: 'error' });
      dispatch(handleError({ error }));
    } finally {
      setSaving(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={retryLoad}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      );
    }

    if (ndcOptions.length === 0) {
      return (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          No NDCs found for this product.
        </Typography>
      );
    }

    return (
      <RadioGroup value={selectedNdc} onChange={(e) => setSelectedNdc(e.target.value)}>
        {ndcOptions.map((option) => (
          <FormControlLabel
            key={option.ndc}
            value={option.ndc}
            control={<Radio />}
            disabled={saving}
            sx={{ alignItems: 'flex-start', py: 0.5 }}
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component='span' sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {option.ndc}
                  </Box>
                  {option.isDefault && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        px: 0.75,
                        height: 18,
                        borderRadius: '4px',
                        bgcolor: '#FEF3C7',
                        border: '1px solid #FCD34D',
                        color: '#92400E',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                      }}
                    >
                      <StarIcon sx={{ fontSize: '0.7rem' }} />
                      DEFAULT
                    </Box>
                  )}
                </Box>
                <Box component='span' sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {option.drugName}
                  {option.strength ? ` · ${option.strength}` : ''}
                </Box>
                {option.manufacturer && (
                  <Box component='span' sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Manufacturer: {option.manufacturer}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit Default NDC</DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
          Choose which NDC is the default to dispense for product{' '}
          <Box component='span' sx={{ fontWeight: 700 }}>
            {productId}
          </Box>
          . The default applies across all facilities, and does not require the NDC to be in stock.
        </Typography>

        {renderBody()}
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={saving || loading || !selectedNdc || selectedNdc === currentDefaultNdc}
          startIcon={saving ? <CircularProgress size={16} color='inherit' /> : null}
        >
          Save Default
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditDefaultNdcDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  productId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
EditDefaultNdcDialog.defaultProps = {
  productId: null,
  onSaved: null,
};

export default EditDefaultNdcDialog;
